import React, { useEffect, useMemo, useRef, useState } from "react";
import { Accordion, Box, Button, DatePicker, Flex, Heading, Notification, Table, Text, TextField } from "@telia/teddy";
import { MONTH_NAMES_NB } from "../../../../../constants";
import {
    API_URL_MANAGER_COMMISSION_NUMBERS,
    API_URL_MANAGER_COMMISSION_NUMBERS_REPORT,
    API_URL_MANAGER_COMMISSION_NUMBERS_REPORT_DETAILS,
    API_URL_SUPERADMIN_COMMISSION_NUMBERS,
    API_URL_SUPERADMIN_COMMISSION_NUMBERS_REPORT,
    API_URL_SUPERADMIN_COMMISSION_NUMBERS_REPORT_DETAILS,
} from "../../api";
import { retailerFetch } from "../../retailerFetch";

type CommissionSbiTotal = {
    amount: number;
    sbi: number;
    settlementDate: string;
    total: number;
    vat: number;
};

type CommissionReportDetails = {
    headers: string[];
    rows: Array<Array<number | string>>;
};

const groupByYearAndMonth = (totals: CommissionSbiTotal[]): Record<string, Record<string, CommissionSbiTotal[]>> => {
    const grouped: Record<string, Record<string, CommissionSbiTotal[]>> = {};
    for (const row of totals) {
        const [year, month] = row.settlementDate.split("-");
        grouped[year] ??= {};
        grouped[year][month] ??= [];
        grouped[year][month].push(row);
    }
    return grouped;
};

const buildCommissionRowKey = (dealerCode: string, row: CommissionSbiTotal): string =>
    JSON.stringify({
        dealerCode,
        sbi: row.sbi,
        settlementDate: row.settlementDate,
        amount: row.amount,
        vat: row.vat,
        total: row.total,
    });

const buildCommissionDetailsKey = (dealerCode: string, params: Record<string, string>): string =>
    JSON.stringify({
        dealerCode,
        ...params,
    });

const MANAGER_SCOPE = "SESSION";

const API_URLS = {
    superadmin: {
        totals: API_URL_SUPERADMIN_COMMISSION_NUMBERS,
        report: API_URL_SUPERADMIN_COMMISSION_NUMBERS_REPORT,
        details: API_URL_SUPERADMIN_COMMISSION_NUMBERS_REPORT_DETAILS,
    },
    manager: {
        totals: API_URL_MANAGER_COMMISSION_NUMBERS,
        report: API_URL_MANAGER_COMMISSION_NUMBERS_REPORT,
        details: API_URL_MANAGER_COMMISSION_NUMBERS_REPORT_DETAILS,
    },
};

type CommissionNumbersProps = {
    mode?: "superadmin" | "manager";
};

const CommissionNumbers: React.FC<CommissionNumbersProps> = ({ mode = "superadmin" }) => {
    const isManagerMode = mode === "manager";
    const apiUrls = API_URLS[mode];
    const setDealerParam = (url: URL, code: string) => {
        if (!isManagerMode) url.searchParams.set("dealerCode", code);
    };
    const [dealerCode, setDealerCode] = useState("");
    const [selectedDealerCode, setSelectedDealerCode] = useState<string | null>(null);
    const [totals, setTotals] = useState<CommissionSbiTotal[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const [gsm, setGsm] = useState("");
    const [imei, setImei] = useState("");
    const [periodStart, setPeriodStart] = useState("");
    const [periodEnd, setPeriodEnd] = useState("");
    const [reportError, setReportError] = useState<string | null>(null);
    const [downloadingKey, setDownloadingKey] = useState<string | null>(null);
    const [openYears, setOpenYears] = useState<string[]>([]);
    const [expandedRowKey, setExpandedRowKey] = useState<string | null>(null);
    const [detailsByKey, setDetailsByKey] = useState<Record<string, CommissionReportDetails>>({});
    const [detailsLoadingKey, setDetailsLoadingKey] = useState<string | null>(null);
    const [detailsErrorByKey, setDetailsErrorByKey] = useState<Record<string, string>>({});
    const totalsRequestIdRef = useRef(0);
    const selectedDealerCodeRef = useRef<string | null>(null);

    const groupedTotals = useMemo(() => groupByYearAndMonth(totals), [totals]);
    const sortedYears = useMemo(() => Object.keys(groupedTotals).sort((a, b) => b.localeCompare(a)), [groupedTotals]);

    useEffect(() => {
        if (sortedYears.length > 0) setOpenYears([sortedYears[0]]);
    }, [sortedYears]);

    useEffect(() => {
        selectedDealerCodeRef.current = selectedDealerCode;
    }, [selectedDealerCode]);

    const loadTotals = async (normalizedDealerCode: string) => {
        const requestId = totalsRequestIdRef.current + 1;
        totalsRequestIdRef.current = requestId;

        setError(null);
        setReportError(null);
        setLoading(true);
        setSelectedDealerCode(null);
        selectedDealerCodeRef.current = null;
        setTotals([]);
        setOpenYears([]);
        setExpandedRowKey(null);
        setDetailsByKey({});
        setDetailsErrorByKey({});
        setDetailsLoadingKey(null);
        setDownloadingKey(null);
        try {
            const url = new URL(apiUrls.totals, window.location.origin);
            setDealerParam(url, normalizedDealerCode);
            const response = await retailerFetch(url.toString());
            const body = (await response.json()) as { error?: string; totals?: CommissionSbiTotal[] };
            if (!response.ok) throw new Error(body.error ?? "Kunne ikke hente provisjonstall");
            if (totalsRequestIdRef.current !== requestId) return;
            selectedDealerCodeRef.current = normalizedDealerCode;
            setSelectedDealerCode(normalizedDealerCode);
            setTotals(body.totals ?? []);
        } catch (requestError) {
            if (totalsRequestIdRef.current !== requestId) return;
            setError(requestError instanceof Error ? requestError.message : "Kunne ikke hente provisjonstall");
        } finally {
            if (totalsRequestIdRef.current === requestId) {
                setLoading(false);
            }
        }
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const normalizedDealerCode = dealerCode.trim().toUpperCase();
        if (!normalizedDealerCode) return;
        await loadTotals(normalizedDealerCode);
    };

    useEffect(() => {
        if (isManagerMode) void loadTotals(MANAGER_SCOPE);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isManagerMode]);

    const downloadReport = async (key: string, params: Record<string, string>) => {
        if (!selectedDealerCode) return;
        setReportError(null);
        setDownloadingKey(key);
        try {
            const url = new URL(apiUrls.report, window.location.origin);
            setDealerParam(url, selectedDealerCode);
            Object.entries(params).forEach(([paramKey, value]) => url.searchParams.set(paramKey, value));

            const response = await retailerFetch(url.toString());
            if (!response.ok) {
                const body = (await response.json().catch(() => ({}))) as { error?: string };
                throw new Error(body.error ?? "Kunne ikke hente provisjonsrapport");
            }

            const disposition = response.headers.get("Content-Disposition") ?? "";
            const filename = disposition.match(/filename="?([^"]+)"?/)?.[1] ?? "provisjonsrapport.xlsx";

            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            const anchor = document.createElement("a");
            anchor.href = blobUrl;
            anchor.download = filename;
            document.body.appendChild(anchor);
            anchor.click();
            anchor.remove();
            setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
        } catch (requestError) {
            setReportError(requestError instanceof Error ? requestError.message : "Kunne ikke hente provisjonsrapport");
        } finally {
            setDownloadingKey(null);
        }
    };

    // Toggles the inline detail table for a given SBI row. Details are fetched once and cached per
    // row key so re-expanding an already-loaded row doesn't trigger a new request.
    const toggleReportDetails = async (rowKey: string, detailsKey: string, params: Record<string, string>) => {
        const requestDealerCode = selectedDealerCode;
        if (!requestDealerCode) return;
        if (expandedRowKey === rowKey) {
            setExpandedRowKey(null);
            return;
        }
        setExpandedRowKey(rowKey);
        if (detailsByKey[detailsKey] || detailsLoadingKey === detailsKey) return;

        setDetailsLoadingKey(detailsKey);
        setDetailsErrorByKey((prev) => {
            const next = { ...prev };
            delete next[detailsKey];
            return next;
        });
        try {
            const url = new URL(apiUrls.details, window.location.origin);
            setDealerParam(url, requestDealerCode);
            Object.entries(params).forEach(([paramKey, value]) => url.searchParams.set(paramKey, value));

            const response = await retailerFetch(url.toString());
            const body = (await response.json()) as CommissionReportDetails & { error?: string };
            if (!response.ok) throw new Error(body.error ?? "Kunne ikke hente rapportdetaljer");
            if (selectedDealerCodeRef.current !== requestDealerCode) return;

            setDetailsByKey((prev) => ({ ...prev, [detailsKey]: { headers: body.headers, rows: body.rows } }));
        } catch (requestError) {
            if (selectedDealerCodeRef.current !== requestDealerCode) return;
            setDetailsErrorByKey((prev) => ({
                ...prev,
                [detailsKey]: requestError instanceof Error ? requestError.message : "Kunne ikke hente rapportdetaljer",
            }));
        } finally {
            if (selectedDealerCodeRef.current === requestDealerCode) {
                setDetailsLoadingKey(null);
            }
        }
    };

    return (
        <div className="retailer-dashboard-wrapper">
            <Heading as="h1" variant="title-300" mb="400">
                {isManagerMode ? "Provisjon" : "Provisjonstall"}
            </Heading>

            {!isManagerMode && (
                <Box mb="500" style={{ maxWidth: "600px" }}>
                    <form onSubmit={handleSubmit}>
                        <Flex gap="300" align="end">
                            <Box style={{ flex: 1 }}>
                                <TextField>
                                    <TextField.Label>Forhandlerkode</TextField.Label>
                                    <TextField.Input
                                        value={dealerCode}
                                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                            setDealerCode(event.target.value)
                                        }
                                        placeholder="Skriv inn forhandlerkode"
                                        autoComplete="off"
                                    />
                                </TextField>
                            </Box>
                            <Button type="submit" disabled={!dealerCode.trim() || loading}>
                                {loading ? "Henter..." : "Vis provisjonstall"}
                            </Button>
                        </Flex>
                    </form>
                </Box>
            )}

            {isManagerMode && loading && <Text>Henter provisjonstall...</Text>}

            {error && (
                <Notification type="tile" variant="error" mb="300">
                    <Notification.Icon />
                    <Notification.Text>{error}</Notification.Text>
                </Notification>
            )}

            {selectedDealerCode && totals.length > 0 && (
                <Box mb="600">
                    <Heading as="h2" variant="title-200" mt="400" mb="200">
                        {isManagerMode ? (
                            "Provisjonstall"
                        ) : (
                            <>
                                Provisjonstall for <strong>{selectedDealerCode}</strong>
                            </>
                        )}
                    </Heading>
                    <Accordion type="multiple" value={openYears} onValueChange={setOpenYears} indented>
                        {sortedYears.map((year) => {
                            const months = groupedTotals[year];
                            const sortedMonths = Object.keys(months).sort((a, b) => b.localeCompare(a));
                            return (
                                <Accordion.Item key={year} value={year}>
                                    <Accordion.Header>
                                        <Accordion.Trigger>{year}</Accordion.Trigger>
                                    </Accordion.Header>
                                    <Accordion.Content>
                                        {sortedMonths.map((month) => (
                                            <Box key={`${year}-${month}`} mb="400">
                                                <Heading as="h3" variant="title-100" mb="200">
                                                    {MONTH_NAMES_NB[Number(month) - 1]} {year}
                                                </Heading>
                                                <Table style={{ width: "100%", tableLayout: "auto" }}>
                                                    <Table.Header>
                                                        <Table.Row>
                                                            <Table.Head>SBI</Table.Head>
                                                            <Table.Head>Beløp</Table.Head>
                                                            <Table.Head>MVA</Table.Head>
                                                            <Table.Head>Total</Table.Head>
                                                            <Table.Head>Oppgjørsdato</Table.Head>
                                                            <Table.Head></Table.Head>
                                                        </Table.Row>
                                                    </Table.Header>
                                                    <Table.Body>
                                                        {months[month].map((row) => {
                                                            const rowKey = buildCommissionRowKey(
                                                                selectedDealerCode,
                                                                row,
                                                            );
                                                            const detailsKey = buildCommissionDetailsKey(
                                                                selectedDealerCode,
                                                                {
                                                                    sbi: String(row.sbi),
                                                                },
                                                            );
                                                            const isExpanded = expandedRowKey === rowKey;
                                                            const details = detailsByKey[detailsKey];
                                                            const detailsErr = detailsErrorByKey[detailsKey];
                                                            return (
                                                                <React.Fragment key={rowKey}>
                                                                    <Table.Row>
                                                                        <Table.Cell>{row.sbi}</Table.Cell>
                                                                        <Table.Cell>{row.amount}</Table.Cell>
                                                                        <Table.Cell>{row.vat}</Table.Cell>
                                                                        <Table.Cell>{row.total}</Table.Cell>
                                                                        <Table.Cell>{row.settlementDate}</Table.Cell>
                                                                        <Table.Cell>
                                                                            <Flex gap="200">
                                                                                <Button
                                                                                    variant="text"
                                                                                    size="sm"
                                                                                    disabled={
                                                                                        detailsLoadingKey === detailsKey
                                                                                    }
                                                                                    onClick={() =>
                                                                                        toggleReportDetails(
                                                                                            rowKey,
                                                                                            detailsKey,
                                                                                            {
                                                                                                sbi: String(row.sbi),
                                                                                            },
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    {detailsLoadingKey === detailsKey
                                                                                        ? "Henter..."
                                                                                        : isExpanded
                                                                                          ? "Skjul detaljer"
                                                                                          : "Se detaljer"}
                                                                                </Button>
                                                                                <Button
                                                                                    variant="text"
                                                                                    size="sm"
                                                                                    disabled={downloadingKey === rowKey}
                                                                                    onClick={() =>
                                                                                        downloadReport(rowKey, {
                                                                                            sbi: String(row.sbi),
                                                                                        })
                                                                                    }
                                                                                >
                                                                                    {downloadingKey === rowKey
                                                                                        ? "Henter..."
                                                                                        : "Last ned"}
                                                                                </Button>
                                                                            </Flex>
                                                                        </Table.Cell>
                                                                    </Table.Row>
                                                                    {isExpanded && (
                                                                        <Table.Row>
                                                                            <Table.Cell colSpan={6}>
                                                                                {detailsErr && (
                                                                                    <Notification
                                                                                        type="tile"
                                                                                        variant="error"
                                                                                        mb="200"
                                                                                    >
                                                                                        <Notification.Icon />
                                                                                        <Notification.Text>
                                                                                            {detailsErr}
                                                                                        </Notification.Text>
                                                                                    </Notification>
                                                                                )}
                                                                                {details &&
                                                                                    details.rows.length === 0 && (
                                                                                        <Text>
                                                                                            Ingen detaljer funnet.
                                                                                        </Text>
                                                                                    )}
                                                                                {details && details.rows.length > 0 && (
                                                                                    <Table
                                                                                        style={{
                                                                                            width: "100%",
                                                                                            tableLayout: "auto",
                                                                                        }}
                                                                                    >
                                                                                        <Table.Header>
                                                                                            <Table.Row>
                                                                                                {details.headers.map(
                                                                                                    (header, index) => (
                                                                                                        <Table.Head
                                                                                                            key={`${header}-${index}`}
                                                                                                        >
                                                                                                            {header}
                                                                                                        </Table.Head>
                                                                                                    ),
                                                                                                )}
                                                                                            </Table.Row>
                                                                                        </Table.Header>
                                                                                        <Table.Body>
                                                                                            {details.rows.map(
                                                                                                (
                                                                                                    detailRow,
                                                                                                    rowIndex,
                                                                                                ) => (
                                                                                                    <Table.Row
                                                                                                        key={rowIndex}
                                                                                                    >
                                                                                                        {detailRow.map(
                                                                                                            (
                                                                                                                cell,
                                                                                                                cellIndex,
                                                                                                            ) => (
                                                                                                                <Table.Cell
                                                                                                                    key={
                                                                                                                        cellIndex
                                                                                                                    }
                                                                                                                >
                                                                                                                    {
                                                                                                                        cell
                                                                                                                    }
                                                                                                                </Table.Cell>
                                                                                                            ),
                                                                                                        )}
                                                                                                    </Table.Row>
                                                                                                ),
                                                                                            )}
                                                                                        </Table.Body>
                                                                                    </Table>
                                                                                )}
                                                                            </Table.Cell>
                                                                        </Table.Row>
                                                                    )}
                                                                </React.Fragment>
                                                            );
                                                        })}
                                                    </Table.Body>
                                                </Table>
                                            </Box>
                                        ))}
                                    </Accordion.Content>
                                </Accordion.Item>
                            );
                        })}
                    </Accordion>
                </Box>
            )}

            {selectedDealerCode && (
                <Box>
                    <Heading as="h2" variant="title-200" mb="200">
                        Detaljert rapport
                    </Heading>
                    <Text mb="300">
                        {isManagerMode ? (
                            "Last ned en detaljert provisjonsrapport (Excel) for din butikk filtrert på ett av kriteriene under."
                        ) : (
                            <>
                                Last ned en detaljert provisjonsrapport (Excel) for forhandlerkode{" "}
                                <strong>{selectedDealerCode}</strong> filtrert på ett av kriteriene under.
                            </>
                        )}
                    </Text>

                    {reportError && (
                        <Notification type="tile" variant="error" mb="300">
                            <Notification.Icon />
                            <Notification.Text>{reportError}</Notification.Text>
                        </Notification>
                    )}

                    <Flex direction="column" gap="400">
                        <Flex gap="300" align="end">
                            <Box style={{ width: "300px" }}>
                                <TextField>
                                    <TextField.Label>Telefonnummer</TextField.Label>
                                    <TextField.Input
                                        value={gsm}
                                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                            setGsm(event.target.value)
                                        }
                                        placeholder="F.eks. 91234567"
                                        autoComplete="off"
                                    />
                                </TextField>
                            </Box>
                            <Button
                                disabled={!gsm.trim() || downloadingKey === "gsm"}
                                onClick={() => downloadReport("gsm", { gsm: gsm.trim() })}
                            >
                                {downloadingKey === "gsm" ? "Henter..." : "Last ned rapport"}
                            </Button>
                        </Flex>

                        <Flex gap="300" align="end">
                            <Box style={{ width: "300px" }}>
                                <Flex direction="column" gap="200">
                                    <DatePicker
                                        label="Fra dato"
                                        value={periodStart}
                                        onSelectedDate={(date) =>
                                            setPeriodStart(date ? date.toISOString().slice(0, 10) : "")
                                        }
                                    />
                                    <DatePicker
                                        label="Til dato"
                                        value={periodEnd}
                                        min={periodStart}
                                        onSelectedDate={(date) =>
                                            setPeriodEnd(date ? date.toISOString().slice(0, 10) : "")
                                        }
                                    />
                                </Flex>
                            </Box>
                            <Button
                                disabled={!periodStart || !periodEnd || downloadingKey === "period"}
                                onClick={() => downloadReport("period", { periodStart, periodEnd })}
                            >
                                {downloadingKey === "period" ? "Henter..." : "Last ned rapport"}
                            </Button>
                        </Flex>

                        <Flex gap="300" align="end">
                            <Box style={{ width: "300px" }}>
                                <TextField>
                                    <TextField.Label>IMEI</TextField.Label>
                                    <TextField.Input
                                        value={imei}
                                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                            setImei(event.target.value)
                                        }
                                        placeholder="Skriv inn IMEI"
                                        autoComplete="off"
                                    />
                                </TextField>
                            </Box>
                            <Button
                                disabled={!imei.trim() || downloadingKey === "imei"}
                                onClick={() => downloadReport("imei", { imei: imei.trim() })}
                            >
                                {downloadingKey === "imei" ? "Henter..." : "Last ned rapport"}
                            </Button>
                        </Flex>
                    </Flex>
                </Box>
            )}
        </div>
    );
};

export default CommissionNumbers;
